import axios, { CanceledError, type CancelToken, type CancelTokenSource } from 'axios'
import { makeAutoObservable, runInAction } from 'mobx'
import { debounce } from 'lodash'
import { logger } from 'shared/lib'

export type IEnrollSearchFetchParams = {
  term: string
  offset: number
  limit: number
  cancelToken: CancelToken | undefined
}

type IEnrollSearchStoreConfig<TItem, TKey extends number | string> = {
  perPage?: number
  getKey: (item: TItem) => TKey
  fetchItems: (params: IEnrollSearchFetchParams) => Promise<{ items: TItem[]; total: number }>
}

const DEBOUNCE_MS = 500

export class EnrollSearchStore<TItem, TKey extends number | string = number> {
  private _offset = 0
  private readonly _perPage: number
  private _total = 0
  private _term = ''
  private _loading = true
  private _initialLoading = true
  private _cancelTokenSource: CancelTokenSource | null = null
  private _items = new Map<TKey, TItem>()

  constructor(private _config: IEnrollSearchStoreConfig<TItem, TKey>) {
    this._perPage = _config.perPage ?? 10
    makeAutoObservable(this)
  }

  get hasMore() {
    return this._loading || this._offset < this._total
  }

  get loading() {
    return this._loading
  }

  get initialLoading() {
    return this._initialLoading
  }

  get termItems() {
    return Array.from(this._items.values())
  }

  get termItemsEmpty() {
    return !this._items.size
  }

  searchItems = async (term: string) => {
    this._term = term
    this._tryMakeRequest(async () => {
      runInAction(() => this._items.clear())

      const { items, total } = await this._config.fetchItems({
        term: this._term,
        offset: 0,
        limit: this._perPage,
        cancelToken: this._cancelTokenSource?.token,
      })

      runInAction(() => {
        this._offset = items.length
        this._total = total
        items.forEach((item) => this._items.set(this._config.getKey(item), item))
      })
    })
  }

  loadMore = () =>
    this._tryMakeRequest(async () => {
      const { items, total } = await this._config.fetchItems({
        term: this._term,
        offset: this._offset,
        limit: this._perPage,
        cancelToken: this._cancelTokenSource?.token,
      })

      runInAction(() => {
        this._offset += items.length
        this._total = total
        items.forEach((item) => this._items.set(this._config.getKey(item), item))
      })
    })

  reset = () => {
    this._offset = 0
    this._total = 0
    this._term = ''
    this._loading = true
    this._initialLoading = true
    this._items.clear()
    this._cancelTokenSource?.cancel()
  }

  private _initCancelTokenSource = () => {
    if (this._cancelTokenSource) this._cancelTokenSource.cancel()
    this._cancelTokenSource = axios.CancelToken.source()
  }

  private _tryMakeRequest = debounce(async (action: () => Promise<void>) => {
    runInAction(() => {
      this._loading = true
      this._initCancelTokenSource()
    })

    try {
      await action()
      runInAction(() => {
        this._loading = false
        this._initialLoading = false
      })
    } catch (error) {
      runInAction(() => {
        this._loading = error instanceof CanceledError
        this._initialLoading = false
      })
      logger.error(error)
    }
  }, DEBOUNCE_MS)
}
